import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ChevronRight,
  Copy,
  Gamepad2,
  Radio,
  Trophy,
  UserPlus,
} from 'lucide-react';
import HotGamesSlider from './HotGamesSlider';
import MiniRouletteLogo from './MiniRouletteLogo';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const WINNERS = ['Maya P.', 'Andre T.', 'Sofia L.', 'Jayden B.', 'Omar J.', 'Zara Q.'];
const WIN_GAMES = ['Game Vault', 'Fire Kirin', 'Juwa 2.0', 'Ultra Panda', 'VB Link'];

function nextWin() {
  return {
    id: `${Date.now()}-${Math.random()}`,
    name: WINNERS[Math.floor(Math.random() * WINNERS.length)],
    game: WIN_GAMES[Math.floor(Math.random() * WIN_GAMES.length)],
    amount: Math.round(180 + Math.random() * 1650),
  };
}

function LiveWinRail() {
  const [win, setWin] = useState(nextWin);

  useEffect(() => {
    const timer = window.setInterval(() => setWin(nextWin()), 4200);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className="vl-lobby-win" data-testid="recent-wins">
      <span className="vl-lobby-win__live"><Radio /> LIVE</span>
      <span key={win.id} className="vl-lobby-win__message">
        <Trophy /> {win.name} · {win.game}
      </span>
      <strong>+${win.amount}</strong>
    </div>
  );
}

export default function HomeTab({
  user,
  data,
  copyReferral,
  showReferral = true,
  onReferralClick,
  onGameClick,
  onDeposit,
  onWithdraw,
}) {
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [bannerIdx, setBannerIdx] = useState(0);
  const banners = (data.homeBanners || data.announcements || []).filter((item) => item?.image_url);
  const roulettePath = tenantSlug ? `/${tenantSlug}/app/roulette` : '/app/roulette';
  useEffect(() => {
    if (banners.length < 2) return undefined;
    const timer = window.setInterval(() => setBannerIdx((value) => (value + 1) % banners.length), 6000);
    return () => window.clearInterval(timer);
  }, [banners.length]);

  const resolveUrl = (url) => (url?.startsWith('http') ? url : `${API_BASE}${url || ''}`);
  const moveCaptain = (event) => {
    if (event.pointerType === 'touch') return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 18;
    event.currentTarget.style.setProperty('--captain-x', `${x.toFixed(1)}px`);
  };
  const resetCaptain = (event) => event.currentTarget.style.setProperty('--captain-x', '0px');

  return (
    <div className="vl-game-lobby" data-testid="home-tab">
      <section
        className="vl-lobby-stage"
        data-testid="casino-hero"
        onPointerMove={moveCaptain}
        onPointerLeave={resetCaptain}
      >
        <div className="vl-lobby-stage__world" aria-hidden="true">
          <span className="vl-lobby-stage__clouds" />
          <span className="vl-lobby-stage__sea" />
          <span className="vl-lobby-stage__lantern vl-lobby-stage__lantern--one" />
          <span className="vl-lobby-stage__lantern vl-lobby-stage__lantern--two" />
        </div>
        <div className="vl-lobby-stage__moon" aria-hidden="true" />
        <div className="vl-lobby-stage__character" aria-hidden="true">
          <span className="vl-captain-aura" />
          <img src="/vegas-pirate-captain-live.webp" alt="" draggable="false" />
          <img className="vl-captain-live-face" src="/vegas-pirate-captain-live.webp" alt="" draggable="false" />
          <span className="vl-captain-breath" />
        </div>
        <div className="vl-lobby-stage__hud">
          <span className="vl-lobby-stage__live"><i /> PIRATE TABLE</span>
          <h1><span>ARE YOU</span> READY?</h1>
          <button type="button" onClick={() => onGameClick?.()} data-testid="hero-play-games">
            <Gamepad2 /> PLAY NOW <ChevronRight />
          </button>
        </div>
        <div className="vl-lobby-stage__scan" aria-hidden="true" />
      </section>

      <div className="vl-lobby-actions vl-lobby-actions--wallet" aria-label="Wallet actions">
        <button type="button" onClick={onDeposit} data-testid="hero-add-funds">
          <span><ArrowDownToLine /></span><b className="vl-action-word vl-action-word--deposit">DEPOSIT</b>
        </button>
        <button type="button" onClick={onWithdraw} data-testid="hero-redeem">
          <span><ArrowUpFromLine /></span><b className="vl-action-word vl-action-word--withdraw">WITHDRAW</b>
        </button>
      </div>

      <LiveWinRail />

      {(data.games || []).length > 0 && (
        <section className="vl-lobby-games">
          <div className="vl-lobby-title">
            <span className="vl-feature-grid__title" aria-label="Hot Games" />
            <button type="button" onClick={() => onGameClick?.()}>ALL GAMES <ChevronRight /></button>
          </div>
          <HotGamesSlider games={data.games} onGameClick={onGameClick} />
        </section>
      )}

      <div className="vl-lobby-quests">
        <button type="button" onClick={() => navigate(roulettePath, { replace: true })} data-testid="home-roulette-cta">
          <span className="vl-lobby-quests__icon"><MiniRouletteLogo size={40} /></span>
          <span><small>DAILY TREASURE WHEEL</small><b>ROULETTE</b></span>
          <ChevronRight />
        </button>
        {showReferral && user?.referral_code && (
          <button type="button" onClick={onReferralClick} data-testid="home-referral-card">
            <span className="vl-lobby-quests__icon"><UserPlus /></span>
            <span><small>GROW YOUR CREW</small><b>REFER &amp; EARN</b></span>
            <span
              role="button"
              tabIndex={0}
              className="vl-lobby-copy"
              onClick={(event) => { event.stopPropagation(); copyReferral?.(); }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  copyReferral?.();
                }
              }}
              data-testid="copy-referral-btn"
              aria-label="Copy referral code"
            >
              <Copy />
            </span>
          </button>
        )}
      </div>

      {banners.length > 0 && (
        <div className="vl-lobby-banner" data-testid="home-banners">
          <img
            src={resolveUrl(banners[bannerIdx]?.image_url)}
            alt={banners[bannerIdx]?.title || 'Vegas Lobby event'}
            loading="lazy"
            decoding="async"
          />
          {banners.length > 1 && (
            <div>{banners.map((banner, index) => (
              <button
                key={banner.id || index}
                type="button"
                aria-label={`Show event ${index + 1}`}
                data-active={index === bannerIdx ? 'true' : 'false'}
                onClick={() => setBannerIdx(index)}
              />
            ))}</div>
          )}
        </div>
      )}
    </div>
  );
}
